/**
 * HabitList — list of active habits with today's completion status and daily summary.
 *
 * Reads from useHabitStore: habits, todayCompletions, loadHabits, loadTodayCompletions
 * On mount: calls loadHabits() and loadTodayCompletions()
 * Shows daily summary at top: "X of Y complete today"
 * Renders list of HabitItem components
 * If no habits: renders EmptyState with CTA to create first habit
 *
 * @see Requirements 8.2, 8.3, 8.9
 */

import { useEffect, useCallback, useState } from 'react';
import { useHabitStore } from '../../stores';
import { HabitItem } from './HabitItem';
import { HabitMonthCalendar } from './HabitMonthCalendar';
import { EmptyState } from '../ui/EmptyState';
import { Badge } from '../ui/Badge';

export interface HabitListProps {
  /** Called when user requests to create a new habit. */
  onCreateHabit?: () => void;
  /** Called when user clicks a habit to view details. */
  onHabitClick?: (habitId: string) => void;
}

export function HabitList({ onCreateHabit, onHabitClick }: HabitListProps) {
  const habits = useHabitStore((s) => s.habits);
  const todayCompletions = useHabitStore((s) => s.todayCompletions);
  const loadHabits = useHabitStore((s) => s.loadHabits);
  const loadTodayCompletions = useHabitStore((s) => s.loadTodayCompletions);
  const toggleCompletion = useHabitStore((s) => s.toggleCompletion);
  const isLoading = useHabitStore((s) => s.isLoading);

  // Per-habit calendar expand/collapse state. Calendars are expanded by default:
  // a habitId is considered collapsed only when explicitly set to `true` here.
  const [collapsedCalendars, setCollapsedCalendars] = useState<Record<string, boolean>>({});

  const isCalendarExpanded = useCallback(
    (habitId: string) => collapsedCalendars[habitId] !== true,
    [collapsedCalendars]
  );

  const handleToggleCalendar = useCallback((e: React.MouseEvent, habitId: string) => {
    // Prevent the row click (habit selection) and completion toggle from firing.
    e.stopPropagation();
    setCollapsedCalendars((prev) => ({
      ...prev,
      // Flip collapsed state: currently expanded (not `true`) -> collapse (`true`).
      [habitId]: prev[habitId] !== true,
    }));
  }, []);

  // Load habits and today's completions on mount
  useEffect(() => {
    loadHabits();
    loadTodayCompletions();
  }, [loadHabits, loadTodayCompletions]);

  const handleToggle = useCallback(
    (habitId: string) => {
      toggleCompletion(habitId);
    },
    [toggleCompletion]
  );

  const handleHabitClick = useCallback(
    (habitId: string) => {
      onHabitClick?.(habitId);
    },
    [onHabitClick]
  );

  // Daily summary counts
  const completedCount = todayCompletions.length;
  const totalCount = habits.length;

  // Loading state
  if (isLoading && habits.length === 0) {
    return (
      <div className="flex items-center justify-center py-12" aria-busy="true" aria-label="Loading habits">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Empty state
  if (habits.length === 0) {
    return (
      <EmptyState
        title="No habits yet"
        description="Start building consistent routines by creating your first daily habit."
        icon={
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="24" cy="24" r="20" stroke="var(--color-primary)" strokeWidth="2" strokeDasharray="4 3" />
            <path d="M24 14v20M14 24h20" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" />
          </svg>
        }
        action={
          onCreateHabit
            ? { label: 'Create First Habit', onClick: onCreateHabit }
            : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Daily summary */}
      <div
        className="flex items-center justify-between px-3 py-2"
        aria-label={`${completedCount} of ${totalCount} habits complete today`}
      >
        <span className="text-sm text-text-secondary font-medium">
          Today&apos;s Progress
        </span>
        <Badge
          text={`${completedCount} of ${totalCount} complete`}
          variant="filled"
          size="sm"
          aria-live="polite"
        />
      </div>

      {/* Habit list */}
      <div role="list" aria-label="Active habits">
        {habits.map((habit) => {
          const expanded = isCalendarExpanded(habit.habitId);
          const regionId = `habit-calendar-${habit.habitId}`;
          return (
            <div
              key={habit.habitId}
              className="flex flex-col gap-2 py-2 border-b border-border last:border-b-0"
            >
              <HabitItem
                habit={habit}
                isCompletedToday={todayCompletions.includes(habit.habitId)}
                onToggle={() => handleToggle(habit.habitId)}
                onClick={() => handleHabitClick(habit.habitId)}
              />

              {/* Inline calendar toggle */}
              <button
                type="button"
                onClick={(e) => handleToggleCalendar(e, habit.habitId)}
                aria-expanded={expanded}
                aria-controls={regionId}
                aria-label={`${expanded ? 'Hide' : 'Show'} completion calendar for ${habit.name}`}
                className={[
                  'self-start flex items-center gap-1 px-2 py-1 ml-1 rounded',
                  'text-xs text-text-secondary hover:bg-hover-bg',
                  'transition-colors duration-default ease-default',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                ].join(' ')}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className={[
                    'transition-transform duration-default ease-default',
                    expanded ? 'rotate-180' : 'rotate-0',
                  ].join(' ')}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
                <span>{expanded ? 'Hide calendar' : 'Show calendar'}</span>
              </button>

              {/* Collapsible inline calendar */}
              {expanded && (
                <div
                  id={regionId}
                  role="region"
                  aria-label={`Completion calendar for ${habit.name}`}
                  className="px-3 pb-2 bg-bg-card rounded-md"
                >
                  <HabitMonthCalendar habit={habit} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

HabitList.displayName = 'HabitList';
